
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import ConsumerHeader from "@/components/ConsumerHeader";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [verifying, setVerifying] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState<any>(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const paymentType = searchParams.get('penalty');
    
    console.log('PaymentSuccess params:', { sessionId, paymentType });
    
    if (sessionId && paymentType === 'paid') {
      verifyPenaltyPayment(sessionId);
    } else if (sessionId) {
      // Handle other payment types if needed
      setVerifying(false);
    } else {
      setVerifying(false);
    }
  }, [searchParams]);

  const verifyPenaltyPayment = async (sessionId: string) => {
    setVerifying(true);
    
    try {
      console.log('Verifying penalty payment with session ID:', sessionId);
      
      const { data, error } = await supabase.functions.invoke('verify-penalty-payment', {
        body: { sessionId }
      });

      if (error) {
        console.error('Penalty payment verification error:', error);
        throw error;
      }

      console.log('Penalty payment verified successfully:', data);
      setPaymentDetails(data);
      
      toast({
        title: "Payment Successful",
        description: "Your penalty balance has been cleared.",
      });
      
    } catch (error) {
      console.error('Penalty payment verification failed:', error);
      toast({
        title: "Verification Error",
        description: "There was an issue verifying your payment. Please contact support if you were charged.",
        variant: "destructive"
      });
    } finally {
      setVerifying(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ConsumerHeader />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifying Payment</h2>
            <p className="text-gray-600">Please wait while we confirm your payment...</p>
          </div>
        </div>
      </div>
    );
  }

  const isPenaltyPayment = searchParams.get('penalty') === 'paid';

  return (
    <div className="min-h-screen bg-gray-50">
      <ConsumerHeader />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Card className="text-center">
          <CardHeader>
            <div className="mx-auto mb-4">
              <CheckCircle className="h-16 w-16 text-green-500" />
            </div>
            <CardTitle className="text-2xl text-green-600">Payment Successful!</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {isPenaltyPayment ? (
              <>
                <p className="text-gray-600">
                  Your penalty balance has been successfully paid and cleared.
                </p>
                
                {paymentDetails && (
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-700">Payment Type:</span>
                      <span className="text-gray-900">Penalty Balance</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-700">Amount Paid:</span>
                      <span className="text-gray-900">£{paymentDetails.amountPaid?.toFixed(2) || '0.00'}</span>
                    </div>
                  </div>
                )}
                
                <p className="text-sm text-gray-500">
                  You can now proceed with your bookings without any restrictions.
                </p>
              </>
            ) : (
              <>
                <p className="text-gray-600">
                  Your payment has been successfully processed.
                </p>
                <p className="text-sm text-gray-500">
                  You will receive a confirmation email shortly with all the details.
                </p>
              </>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => navigate("/dashboard")} className="flex items-center space-x-2">
                <span>Go to Dashboard</span>
              </Button>
              
              <Button variant="outline" onClick={() => navigate("/")} className="flex items-center space-x-2">
                <span>Continue Browsing</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentSuccess;
